import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getPropertyPhoto } from "../api/client";

/**
 * ExteriorViewLink — inline "Google Street View →" trigger shown right after the
 * property address (wraps under it on mobile), plus a modal that shows the curb
 * photo without leaving the page.
 *
 * Shown only when Street View coverage exists (nothing renders otherwise, so the
 * header stays clean). The modal is portaled to <body> and covers the page with
 * a dark backdrop while open, so the Amazon Location map is not on-screen with
 * the Street View image. A "© Google" credit + an "Open full Street View" link
 * (Google's own interactive panorama) ride in the modal footer.
 */

interface Props {
  placeId: string;
}

export function ExteriorViewLink({ placeId }: Props) {
  const [photo, setPhoto] = useState<{ img?: string; link?: string } | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPhoto(null);
    // 640x640 → the modal image; a sized request also yields the deep-link.
    getPropertyPhoto(placeId, "640x640")
      .then((p) => {
        if (!cancelled && p.available && p.image_url) {
          setPhoto({ img: p.image_url, link: p.streetview_url });
        }
      })
      .catch(() => {
        /* no CTA when coverage can't be resolved */
      });
    return () => {
      cancelled = true;
    };
  }, [placeId]);

  // Nothing while loading or when there's no coverage — keeps the header tidy.
  if (!photo?.img) return null;

  return (
    <>
      <button
        type="button"
        className="streetview-cta"
        onClick={() => setOpen(true)}
      >
        Google Street View →
      </button>
      {open && (
        <StreetViewModal
          img={photo.img}
          link={photo.link}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function StreetViewModal({
  img,
  link,
  onClose,
}: {
  img: string;
  link?: string;
  onClose: () => void;
}) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Lock background scroll while the modal is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div
      className="sv-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Street View"
    >
      <div className="sv-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="sv-modal-close"
          onClick={onClose}
          aria-label="Close Street View"
        >
          ×
        </button>
        {failed ? (
          <p className="sv-modal-fallback">Street View image could not load.</p>
        ) : (
          <img
            className="sv-modal-img"
            src={img}
            alt="Street-facing exterior of the property"
            onError={() => setFailed(true)}
          />
        )}
        <div className="sv-modal-footer">
          <span className="sv-modal-credit">© Google</span>
          {link && (
            <a href={link} target="_blank" rel="noopener noreferrer">
              Open full Street View ↗
            </a>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * ExteriorThumbnail — small Street View exterior thumbnail for the Saved
 * Properties rows (curb-photo Req 7). The Saved page has no geographic map on
 * screen (only the Tilt Map quadrant chart), so embedding the image here is
 * fine; a "© Google" credit rides beneath it and clicking opens full Street
 * View. Neutral placeholder while loading, on no coverage, or on any error.
 */
export function ExteriorThumbnail({ placeId }: { placeId: string }) {
  const [photo, setPhoto] = useState<{ img: string; link?: string } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPhoto(null);
    setFailed(false);
    getPropertyPhoto(placeId, "200x200")
      .then((p) => {
        if (!cancelled && p.available && p.image_url) {
          setPhoto({ img: p.image_url, link: p.streetview_url });
        }
      })
      .catch(() => {
        /* placeholder on failure */
      });
    return () => {
      cancelled = true;
    };
  }, [placeId]);

  if (!photo || failed) {
    return <div className="saved-thumb-placeholder" role="img" aria-label="No exterior photo" />;
  }

  const img = (
    <img
      className="saved-thumb"
      src={photo.img}
      alt="Property exterior"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );

  return (
    <figure className="saved-thumb-figure">
      {photo.link ? (
        <a
          href={photo.link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title="Open in Google Street View"
        >
          {img}
        </a>
      ) : (
        img
      )}
      <figcaption className="saved-thumb-credit">© Google</figcaption>
    </figure>
  );
}
